/*
 * Module for the user details modal. Adapted to work for both landlord and tenant users.
 */

define([
  "jquery",
  "marionette",
  "messaging/messenger",
  "modules/admin/userAdministration/userDetails/userDetailsModalView",
  "modules/admin/userAdministration/userDetails/basicUserInfoDisplay/basicUserInfoModule",
  "modules/admin/userAdministration/userDetails/kbSelectDisplay/kbSelectModule",
  "modules/admin/userAdministration/userDetails/roleMapDisplay/roleMapModule",
  "modules/admin/userAdministration/userDetails/touchpointMapDisplay/touchpointMapModule",
  "modules/admin/userAdministration/userDetails/contextMapDisplay/contextMapModule",
  "modules/admin/userAdministration/usersModel",
], function (
  $,
  Marionette,
  Messenger,
  UserDetailsModalView,
  BasicUserInfoModule,
  KbSelectModule,
  RoleMapModule,
  TouchpointMapModule,
  ContextMapModule,
  UsersModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "userDetailsModal";
      this.editMode = options.editMode;
    },
    show: async function (user) {
      this.layout = new UserDetailsModalView.userDetailsModalLayout({ model: user });

      //set up sub modules
      this.basicUserInfoModule = this.addSubModule(
        new BasicUserInfoModule({ region: this.layout.basicUserInfoDiv }),
      );

      if (user.isTenantUser()) {
        // Add tenant user specific subModules
        this.kbSelectModule = this.addSubModule(
          new KbSelectModule({ region: this.layout.kbSelectDiv }),
        );
        let tenantKbRoles;
        if (!user.kbRoles() || user.kbRoles().length === 0) {
          const tenant = Messenger.reqres.request("active:tenant");
          tenantKbRoles = Messenger.reqres.request("tenant:kbRoles", tenant.id);
          await tenantKbRoles.onSyncedPromise();
        }
        //To give the roleMapModule the roles sub-model of the user, we simply pass it user.roles()
        //This gives it all roles for all KBs.  The module then sorts things out by KB
        this.roleMapModule = this.addSubModule(
          new RoleMapModule({
            region: this.layout.roleMapDiv,
            roleMap: user.roles(),
            kbRoles: user.kbRoles(),
            tenantKbRoles: tenantKbRoles,
          }),
        );
        this.touchpointMapModule = this.addSubModule(
          new TouchpointMapModule({
            region: this.layout.touchpointMapDiv,
            touchpointMap: user.touchpoints(),
          }),
        );
        this.contextMapModule = this.addSubModule(
          new ContextMapModule({
            region: this.layout.contextMapDiv,
            editMode: this.editMode,
            contextMap: user.contexts(),
          }),
        );
        //get the tenant KBs
        const tenantId = Messenger.reqres.request("active:tenantId");
        this.tenantKbs = Messenger.reqres.request("tenant:kbs", tenantId);
      }

      this.layout.on(
        "done",
        function () {
          //Save the model, and only close the modal if the save is successful.
          //Otherwise, display appropriate error messages and allow the user to
          //correct their error.
          if (Messenger.reqres.request("save:textbox")) {
            if (!Messenger.reqres.request("tenant:needsPassword")) {
              user.set("password", UsersModel.EMPTY_PASSWORD_PLACEHOLDER);
              user.set("reenterPassword", UsersModel.EMPTY_PASSWORD_PLACEHOLDER);
            }
            const validationErrors = user.validate();
            if (validationErrors) {
              this._onUserInvalid(user, validationErrors);
              return;
            }
            user.saveModel({
              success: function () {
                Messenger.vent.trigger("close:modal");
                // Clear out the password after a successful save
                user.set(
                  {
                    password: "",
                    reenterPassword: "",
                  },
                  {
                    silent: true,
                  },
                );
              },
              error: function (dummy, response) {
                let errorMessage = Messenger.getErrorFromResponse(response);
                if (response.status === 400 && !errorMessage) {
                  let errorValue = response.responseJSON.value.toString();
                  const tenant = Messenger.reqres.request("active:tenant");
                  if (errorValue === "The user login must be unique") {
                    errorMessage = $.t("admin.userAdministration.uniqueLogin");
                  } else if (errorValue === "The e-mail address is invalid") {
                    errorMessage = $.t("admin.userAdministration.validEmail");
                  } else if (errorValue === "The user must have a name") {
                    errorMessage = $.t("admin.userAdministration.noName");
                  } else if (errorValue === "The user must have a login") {
                    errorMessage = $.t("admin.userAdministration.noLogin");
                  } else if (errorValue === "The user must have a password") {
                    errorMessage = $.t("user.missingPassword");
                  } else if (/The password must be at least \d+ characters long/.test(errorValue)) {
                    errorMessage = $.t("user.shortPassword", tenant.attributes);
                  } else if (errorValue === "You cannot deactivate yourself") {
                    errorMessage = $.t("admin.userAdministration.selfDeactivate");
                  } else if (errorValue === "The password must not contain the user's login ID") {
                    errorMessage = $.t("user.passwordCannotContainUserLogin");
                  } else if (errorValue === "The password must not contain the user's full name") {
                    errorMessage = $.t("user.passwordCannotContainUserFullName");
                  } else if (
                    /The password must contain at least \d+ groups from the following: uppercase letters, lowercase letters, numbers, symbols, and other Unicode characters/.test(
                      errorValue,
                    )
                  ) {
                    errorMessage = $.t("user.weakPassword", tenant.attributes);
                  } else if (
                    /You must wait at least \d+ (day|days) to change the password/.test(errorValue)
                  ) {
                    errorMessage = $.t("user.passwordTooYoung", tenant.attributes);
                  } else if (errorValue === "You cannot reuse that password") {
                    errorMessage = $.t("user.cannotReusePassword");
                  } else {
                    errorMessage = errorValue;
                  }
                }
                Messenger.okDialog(errorMessage);
                //Allow the admin to load up the user later should they wish
                user.isRequested = false;
              },
            });
          }
        },
        this,
      );

      this.layout.on(
        "cancel",
        function () {
          //revert user back to original version
          user.restore();
          if (user.get("userId") === null) {
            user.deleteModel();
          }
          Messenger.vent.trigger("cancel:textbox"); //cancel any open textboxes
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      //if validation fails, call the appropriate functions to display the error
      user.on("invalid", this._onUserInvalid, this);

      //make a backup
      user.backup();

      this.layout.on(
        "show",
        function () {
          this._showSubViews(user);
        },
        this,
      );
      this.showRegion(this.layout);

      this.user = user;
    },
    _onUserInvalid: function (model, error) {
      if ("userLogin" in error) {
        this.basicUserInfoModule.nameError("#userLogin", error["userLogin"]);
      }
      if ("userFullName" in error) {
        this.basicUserInfoModule.nameError("#userFullName", error["userFullName"]);
      }
      if ("userEmail" in error) {
        this.basicUserInfoModule.nameError("#email", error["userEmail"]);
      }
      if ("password" in error) {
        this.basicUserInfoModule.nameError("#password", error["password"]);
      }
      if ("roles" in error) {
        this.layout.roleError(error["roles"]);
      }
    },
    _showSubViews: function (user) {
      user.onSynced(function () {
        this.basicUserInfoModule.show(user);
        if (user.isTenantUser()) {
          this.tenantKbs.onSynced(function () {
            this.kbSelectModule.show(this.tenantKbs);
          }, this);
        }
      }, this);
    },
  });
});
